"use client";

import { Box, IconButton } from "@mui/material";
import { pxToVw } from "@/utils/pxToVw";
import Image from "next/image";
import { MOBILE_BASE } from "@/theme/theme";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import { useRef } from "react";
import type { Swiper as SwiperType } from "swiper";
import { BannerHomeItem } from "@/types/bannerHomes";
import HeroSlide from "@/components/HeroSlide";

// Import Swiper styles
import "swiper/css";

interface HeroSectionProps {
  banners: BannerHomeItem[];
}

export default function HeroBanner({ banners }: HeroSectionProps) {
  const swiperRef = useRef<SwiperType | null>(null);

  // Mapear los datos de Strapi a la estructura de slides
  const slides = banners.map((banner, index) => {
    const imageDesktop = `${process.env.NEXT_PUBLIC_STRAPI_URL}${banner.ImagenEscritorio.url}`;
    const imageMobile = banner.ImagenResponsive?.[0]
      ? `${process.env.NEXT_PUBLIC_STRAPI_URL}${banner.ImagenResponsive[0].url}`
      : imageDesktop;

    // Determinar el tipo de layout
    if (banner.ImagenAnchoCompleto) {
      return {
        id: banner.id,
        type: "overlay" as const,
        imageDesktop,
        imageMobile,
        caption: banner.Titulo,
        buttonUrl: banner.URLBoton,
        buttonText: banner.TextoBoton,
        gtagEvent: banner.GtagEvent,
        isFirst: index === 0,
      };
    } else if (banner.FondoClaroImagenLateral) {
      return {
        id: banner.id,
        type: "columns" as const,
        imageDesktop,
        imageMobile,
        caption: banner.Titulo,
        buttonUrl: banner.URLBoton,
        buttonText: banner.TextoBoton,
        gtagEvent: banner.GtagEvent,
        bgColor: "#d1cfc6",
        textColor: "#44250d" as const,
        buttonVariant: "botonMarron" as const,
        buttonHoverBg: "#44250d",
        buttonHoverColor: "#d1cfc6",
        isFirst: index === 0,
      };
    } else {
      return {
        id: banner.id,
        type: "columns" as const,
        imageDesktop,
        imageMobile,
        caption: banner.Titulo,
        buttonUrl: banner.URLBoton,
        buttonText: banner.TextoBoton,
        gtagEvent: banner.GtagEvent,
        bgColor: "#44250d",
        textColor: "warm1" as const,
        buttonVariant: "botonWarm1" as const,
        buttonHoverBg: "#d1cfc6",
        buttonHoverColor: "#44250d",
        isFirst: index === 0,
      };
    }
  });

  return (
    <Box
      position={"relative"}
      width={"100%"}
      overflow={"hidden"}
      sx={{
        width: "100%",
        aspectRatio: { xs: "393 / 530", md: "1442 / 856" },
        "& .swiper": {
          width: "100%",
          height: "100%",
        },
      }}
    >
      <Swiper
        modules={[Autoplay]}
        onSwiper={(swiper) => (swiperRef.current = swiper)}
        autoplay={
          banners.length > 1
            ? {
                delay: 5000,
                disableOnInteraction: false,
              }
            : false
        }
        loop={banners.length > 1}
        speed={800}
      >
        {slides.map((slide, index) => (
          <SwiperSlide key={slide.id}>
            <HeroSlide
              type={slide.type}
              imageDesktop={slide.imageDesktop}
              imageMobile={slide.imageMobile}
              caption={slide.caption}
              buttonUrl={slide.buttonUrl}
              buttonText={slide.buttonText}
              isFirst={slide.isFirst}
              component={index === 0 ? "h1" : "h2"}
              gtagEvent={slide.gtagEvent}
              {...(slide.type === "columns" && {
                bgColor: slide.bgColor,
                textColor: slide.textColor,
                buttonVariant: slide.buttonVariant,
                buttonHoverBg: slide.buttonHoverBg,
                buttonHoverColor: slide.buttonHoverColor,
              })}
            />
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Botones de navegación - solo mostrar si hay más de un slide */}
      {banners.length > 1 && (
        <>
          {/* Botón anterior (izquierda) */}
          <IconButton
            onClick={() => swiperRef.current?.slidePrev()}
            sx={{
              position: "absolute",
              left: { xs: pxToVw(10, MOBILE_BASE), md: pxToVw(20) },
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 10,
            }}
          >
            <Box
              sx={{
                transform: "rotate(-90deg)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Image src="/UP.svg" alt="Anterior" width={25} height={25} priority />
            </Box>
          </IconButton>

          {/* Botón siguiente (derecha) */}
          <IconButton
            onClick={() => swiperRef.current?.slideNext()}
            sx={{
              position: "absolute",
              right: { xs: pxToVw(10, MOBILE_BASE), md: pxToVw(20) },
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 10,
            }}
          >
            <Box
              sx={{
                transform: "rotate(90deg)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Image src="/UP.svg" alt="Siguiente" width={25} height={25} priority />
            </Box>
          </IconButton>
        </>
      )}
    </Box>
  );
}
